import { useState, useMemo } from "react";
import { Search, User as UserIcon } from "lucide-react";
import { Input } from "@/modern/components/ui/input";
import { Button } from "@/modern/components/ui/button";
import { Checkbox } from "@/modern/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/modern/components/ui/dialog";
import { ScrollArea } from "@/modern/components/ui/scroll-area";
import { UserAvatar } from "@/modern/components/user-avatar";
import { cn } from "@/modern/utils";
import { NOTIFY_TYPE_COLUMN_CONTENT } from "../constants/ownershipConstants";
import type { BoardUser } from "@/react/hooks/entityHooks/useEntityOwnership";

interface UserSelectorDialogProps {
  notifyType: string;
  selectedUserIds: number[];
  boardUsers: Array<BoardUser>;
  onSelectionChange: (notifyType: string, userIds: number[]) => void;
  children: React.ReactNode;
}

export const UserSelectorDialog = ({
  notifyType,
  selectedUserIds,
  boardUsers,
  onSelectionChange,
  children
}: UserSelectorDialogProps) => {
  const [open, setOpen] = useState(false);
  const [localSelection, setLocalSelection] = useState<number[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  const typeConfig = NOTIFY_TYPE_COLUMN_CONTENT[notifyType as keyof typeof NOTIFY_TYPE_COLUMN_CONTENT];

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      // Reset to current selection when opening
      setLocalSelection(selectedUserIds);
      setSearchQuery("");
    }
    setOpen(isOpen);
  };

  const handleToggleUser = (userId: number) => {
    setLocalSelection((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]));
  };

  const handleSave = () => {
    onSelectionChange(notifyType, localSelection);
    setOpen(false);
  };

  // Filter users based on search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return boardUsers;
    const query = searchQuery.toLowerCase();
    return boardUsers.filter((user) => user.name.toLowerCase().includes(query));
  }, [boardUsers, searchQuery]);

  // Check if selection has changed
  const hasSelectionChanged = useMemo(() => {
    if (localSelection.length !== selectedUserIds.length) return true;
    const sortedLocal = [...localSelection].sort();
    const sortedOriginal = [...selectedUserIds].sort();
    return !sortedLocal.every((id, index) => id === sortedOriginal[index]);
  }, [localSelection, selectedUserIds]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="z-[--swivu-bryntum-z-index] max-w-lg p-0 flex flex-col">
        <DialogHeader className="px-6 py-5">
          <DialogTitle className="flex items-center gap-2 text-base font-normal">
            {typeConfig && <typeConfig.icon className="h-5 w-5 text-primary" />}
            {typeConfig?.title} connections
          </DialogTitle>
          <DialogDescription className="pt-1.5">Select users to connect for this relationship</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col px-6 pb-5">
          {/* Search Input */}
          <div className="relative py-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Search Board Users"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-10 bg-background border-input focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>

          {/* User List */}
          <ScrollArea className="h-[400px] mb-4">
            {filteredUsers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <UserIcon className="h-10 w-10 text-muted-foreground/50 mb-3" />
                <p className="text-sm text-muted-foreground">No contacts found</p>
                {searchQuery && <p className="text-xs text-muted-foreground mt-1">Try a different search term</p>}
              </div>
            ) : (
              <div>
                {filteredUsers.map((user, index) => {
                  const isSelected = localSelection.includes(user.id);

                  return (
                    <div
                      key={user.id}
                      className={cn(
                        "flex items-center justify-between gap-3 px-3 py-2.5 cursor-pointer transition-colors rounded-md",
                        "hover:bg-accent",
                        isSelected && "bg-accent",
                        user.archived && "opacity-50",
                        index < filteredUsers.length - 1 && "mb-1"
                      )}
                      onClick={() => handleToggleUser(user.id)}
                    >
                      <UserAvatar user={user} size="default" showName={true} className="max-w-[350px]" />

                      <Checkbox checked={isSelected} onCheckedChange={() => handleToggleUser(user.id)} onClick={(e) => e.stopPropagation()} className="flex-shrink-0" />
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>

          {/* Selection Counter & Action Buttons */}
          <div className="flex items-center justify-between gap-3 pt-5 border-t">
            <span className={cn("text-sm", localSelection.length > 0 ? "text-foreground font-medium" : "text-muted-foreground")}>{localSelection.length} selected</span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setOpen(false)} size="sm">
                Cancel
              </Button>
              <Button onClick={handleSave} size="sm" disabled={!hasSelectionChanged}>
                Save
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};